import React, { useState, useEffect, useCallback } from 'react';
import { X, Bell, Flame, Send, ShieldCheck } from 'lucide-react';
/**
 * Presentación de la aplicación para quien entra por primera vez.
 *
 * Alguien que aterriza en CholloMan ve una grilla de productos con descuentos y
 * asume que es otra página de ofertas más. Lo que la distingue —que las alertas
 * salen a buscar el producto por vos y te avisan por Telegram— no se deduce
 * mirando la portada: hay que contarlo.
 *
 * El texto es la aplicación directa de docs/PROMOCION.md.
 * Si cambia el posicionamiento, se actualizan los dos juntos.
 *
 * Se muestra una sola vez por navegador. La clave lleva versión: si el mensaje
 * cambia de fondo, subir el número lo vuelve a mostrar a todo el mundo, sin
 * tener que inventar otra clave ni dejar la anterior colgando.
 */

const CLAVE_VISTO = 'cholloman_bienvenida_v1';

// Canal de difusión, distinto del bot que manda las alertas personales. El
// canal publica para todos; el bot te escribe sólo a vos.
//
// Hay un canal por catálogo —@ElCholloMan para Argentina, @ElChollomanEs para
// España— y el enlace tiene que seguir al país activo: un canal único acá
// mandaba a todo el mundo, España incluida, al canal argentino.
const CANAL_TELEGRAM_POR_PAIS = {
  AR: process.env.REACT_APP_TELEGRAM_CANAL || 'ElCholloMan',
  ES: process.env.REACT_APP_TELEGRAM_CANAL_ES || 'ElChollomanEs',
};

const yaSeVio = () => {
  try {
    return localStorage.getItem(CLAVE_VISTO) === '1';
  } catch {
    return false;
  }
};

const marcarVisto = () => {
  try {
    localStorage.setItem(CLAVE_VISTO, '1');
  } catch {
    // Modo privado: el banner volverá a aparecer, que es preferible a romper
    // la carga de la página.
  }
};

/** Una de las tres capacidades, con su icono y su explicación. */
const Punto = ({ icono, color, fondo, titulo, children }) => (
  <li className="bienvenida__punto">
    {/* El fondo va incrustado a propósito: es uno por punto, y tres clases para
        tres usos serían más ruido que las tres declaraciones. */}
    <div className="bienvenida__icono" style={{ background: fondo }}>
      {React.cloneElement(icono, { size: 17, color })}
    </div>
    <div className="bienvenida__punto-texto">
      <p className="bienvenida__punto-titulo">{titulo}</p>
      <p className="bienvenida__punto-detalle">{children}</p>
    </div>
  </li>
);

export const BannerBienvenida = ({ country, onCrearAlerta, onVerOfertas }) => {
  const [visible, setVisible] = useState(false);
  const canalTelegram = CANAL_TELEGRAM_POR_PAIS[country] || CANAL_TELEGRAM_POR_PAIS.AR;

  useEffect(() => {
    if (!yaSeVio()) setVisible(true);
  }, []);

  const cerrar = useCallback(() => {
    marcarVisto();
    setVisible(false);
  }, []);

  // Escape cierra, como cualquier diálogo a pantalla completa.
  useEffect(() => {
    if (!visible) return undefined;
    const alPulsar = (e) => { if (e.key === 'Escape') cerrar(); };
    document.addEventListener('keydown', alPulsar);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [visible, cerrar]);

  if (!visible) return null;

  const conAlerta = () => { cerrar(); if (onCrearAlerta) onCrearAlerta(); };
  const conOfertas = () => { cerrar(); if (onVerOfertas) onVerOfertas(); };


  return (
    <div
      id="banner-bienvenida-overlay"
      className="modal-overlay dialogo dialogo--bienvenida"
      onClick={cerrar}
    >
      <div
        id="banner-bienvenida"
        className="modal-caja dialogo__caja bienvenida__caja"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="banner-bienvenida-titulo"
      >
        {/* Cabecera: el personaje y la promesa */}
        <div className="bienvenida__cabecera">
          <button
            id="banner-bienvenida-cerrar"
            onClick={cerrar}
            aria-label="Cerrar"
            className="tap-exempt bienvenida__cerrar"
          >
            <X size={20} />
          </button>

          <div className="bienvenida__marca">
            <img
              src="/logo-cholloman.png"
              alt=""
              className="bienvenida__logo"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="bienvenida__nombre">Chollo<em>Man</em></span>
          </div>

          <h2 id="banner-bienvenida-titulo" className="bienvenida__titulo">
            Llegó tu superhéroe favorito
          </h2>

          <p className="bienvenida__bajada">
            No pierdas tiempo buscando ofertas.{' '}
            <strong>CholloMan las busca por vos.</strong>
          </p>
        </div>

        {/* Cuerpo: qué hace, en orden de importancia */}
        <div className="bienvenida__cuerpo">
          <ul className="bienvenida__lista">
            <Punto icono={<Bell />} color="#FF4500" fondo="#fff1eb" titulo="Alertas que trabajan solas">
              Decinos qué estás buscando y cuánto querés pagar, que te avisamos
              cuando aparezca, aunque hoy no esté en oferta.
            </Punto>

            <Punto icono={<ShieldCheck />} color="#059669" fondo="#ecfdf5" titulo="Descuentos de verdad">
              No todo lo que dice «-50%» es una rebaja real. Las que no lo son
              no llegan a tu pantalla.
            </Punto>

            <Punto icono={<Flame />} color="#d97706" fondo="#fffbeb" titulo="Y si querés mirar vos">
              Ofertas del día, tiendas argentinas y viajes.
            </Punto>
          </ul>

          {/* Acciones */}
          <div className="dialogo__acciones bienvenida__acciones">
            <button
              id="banner-bienvenida-alerta"
              onClick={conAlerta}
              className="boton-dialogo boton-dialogo--principal boton-dialogo--crece"
            >
              <Bell size={15} />
              Crear mi primera alerta
            </button>
            <button
              id="banner-bienvenida-ofertas"
              onClick={conOfertas}
              className="boton-dialogo boton-dialogo--secundario boton-dialogo--crece"
            >
              Ver las ofertas de hoy
            </button>
          </div>

          {/* Canal de difusión: la puerta de entrada sin cuenta */}
          {/* El texto largo se elegía con `isMobile`, y en un portátil —que no
              es «móvil» pero tampoco tiene el ancho de un escritorio— no entraba
              y se salía de la caja. Ahora el texto es uno solo y la caja lo deja
              partirse en dos renglones si hace falta. */}
          <a
            id="banner-bienvenida-telegram"
            href={`https://t.me/${canalTelegram}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={cerrar}
            className="bienvenida__canal"
          >
            <Send size={15} />
            <span>Seguí el canal de Telegram y mirá las ofertas del día sin cuenta</span>
          </a>
        </div>
      </div>
    </div>
  );
};
